import { HttpClient } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import moment from "moment";
import { BehaviorSubject, interval, Observable, Subject, Subscription } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { Asset } from "../classes/asset";
import { AssetRequestPayload } from "../classes/asset-request-payload";
import { AppConfiguration } from "../classes/configuration";
import { Coordinates, GeoLocationObject } from "../classes/geo-location-object";
import { Profile } from "../classes/profile";
import { ASSET_WORKER_TIMEOUT, ASSET_WORKER_TIMER } from "../common/constants";
import { ConfigurationService } from "./configuration.service";
import { CoordinatesService } from "./coordinates.service";
import { LoggerService } from "./logger.service";
import { MsalConfigService } from './msal-config.service';
import { ProfileService } from "./profile.service";

const useImMemoryWebApi = !environment.production && environment.inMemoryWebApiEnabled;
const worker = new Worker(new URL("../workers/asset.worker", import.meta.url));
@Injectable({
  providedIn: "root",
})
export class AssetService implements OnDestroy {
  assetData$: BehaviorSubject<Array<Asset>> = new BehaviorSubject(Array<Asset>());
  position: any;
  coords: any;
  profileRequestData: any;
  assetRequestPayload = new AssetRequestPayload();
  interval: Subscription = new Subscription();
  location = new GeoLocationObject();
  unsubscribe$: Subject<boolean> = new Subject();
  profile: Profile;
  configuration: AppConfiguration;

  constructor(
    private logger: LoggerService,
    private profileService: ProfileService,
    private http: HttpClient,
    private configurationService: ConfigurationService,
    private msalConfigService: MsalConfigService,
    private coordinatesService: CoordinatesService
  ) {
    this.subscribeToConfiguration();
    this.subscribeToProfile();
    this.subscribeToCoordinates();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(true);
    this.unsubscribe$.complete();
  }

  subscribeToConfiguration(): void {
    this.configurationService.getConfiguration()
      .pipe(
        takeUntil(this.unsubscribe$))
      .subscribe((configuration) => {
        this.configuration = configuration;
      });
  }

  updateAssetData(data: Array<Asset>): void {
    this.assetData$.next(data);
  }

  getAssetData(): Observable<Array<Asset>> {
    return this.assetData$.asObservable();
  }

  // toggle API threading
  public startAssetInterval(): void {
    if (useImMemoryWebApi) {
      this.runMainThread();
    } else {
      this.runWorkerThread();
    }
    this.interval = interval(ASSET_WORKER_TIMER).subscribe(() => {
      if (useImMemoryWebApi) {
        this.runMainThread();
      } else {
        this.runWorkerThread();
      }
    });
  }

  public stopWorkerInterval(): void {
    this.interval.unsubscribe();
  }

  // Web Worker Thread PUT
  public runWorkerThread(): any {
    this.mergeRequestData();
    this.logger.debug("Asset called on worker thread", 'worker');
    worker.onmessage = ({ data }) => {
      if (data.error) {
        this.logger.error(`Asset worker error: ${data.message} (status: ${data.status})`);
        return;
      }
      const parsed = JSON.parse(data.data);
      this.updateAssetData(parsed);
      return parsed;
    };
    worker.onerror = (err) => {
      this.logger.error('Asset worker is suffering!', err)
    };
    this.msalConfigService.getAzureToken().subscribe((token: string) => {
      worker.postMessage({
        "url": this.configuration.HostApi.concat('/asset'),
        "timeout": ASSET_WORKER_TIMEOUT,
        "payload": JSON.stringify(this.assetRequestPayload),
        "authHeader": `Bearer ${token}`
      });
    });
  }

  // Main Thread PUT... used when inMemoryWebApi = true
  public runMainThread(): void {
    this.logger.debug("Asset called on main thread", 'worker');
    const headers = { "Content-type": "application/json; charset=utf-8" };
    this.http.put<Array<Asset>>("api/asset", this.assetRequestPayload, { headers, observe: "response" })
      .subscribe((resp) => {
        this.updateAssetData(resp.body);
        this.logger.debug(`PUT Asset response: ${resp.status} ${resp.statusText}`, resp, 'http');
      });
  }

  // public updatePosition(location: GeoLocationObject): void {
  //   if (location) {
  //     this.location = location;
  //     this.logger.debug(`Your Location: ${this.location.latitude}, ${this.location.longitude}`);
  //   }
  // }

  subscribeToCoordinates(): void{
    this.coordinatesService.coordinates$.subscribe((coordinates: Coordinates) => {
      if (coordinates && coordinates.latitude && coordinates.longitude) {
        this.location.latitude = coordinates.latitude;
        this.location.longitude = coordinates.longitude;
        this.logger.debug(`Your Location: ${this.location.latitude}, ${this.location.longitude}`, 'location');
      }
    });
  }


  /** Merges geolocation data into the asset PUT payload */
  private mergeRequestData(): void {
    let vanId = "null";
    const vanProfile = localStorage.getItem('van-profile');
    if(vanProfile !== null){
      let vanProfileParsed = JSON.parse(vanProfile);
      vanId = vanProfileParsed.vanDriverVanNumberPref;
    }
    this.assetRequestPayload.accuracy = this.location.accuracy !==null ? this.location.accuracy.toString() : "null";
    this.assetRequestPayload.altitude = this.location.altitude !==null ? this.location.altitude.toString() : "null";
    this.assetRequestPayload.heading = this.location.heading !==null ? this.location.heading.toString() : "null";
    this.assetRequestPayload.latitude = this.location?.latitude !==null ? this.location?.latitude?.toString() : "null";
    this.assetRequestPayload.longitude = this.location?.longitude !==null ? this.location?.longitude?.toString() : "null";
    this.assetRequestPayload.speed = this.location.speed !==null ? this.location.speed.toString() : "null";
    this.assetRequestPayload.timestamp = moment().format();
    this.assetRequestPayload.type = "geolocation";
    this.assetRequestPayload.gemsId = this.profile.adId;
    this.assetRequestPayload.vanId = vanId;
  }


  subscribeToProfile(): void {
    this.profileService.getProfileObs().pipe(takeUntil(this.unsubscribe$))
      .subscribe((profile) => {
        if (profile) {
          this.profile = profile;
        }
      });
  }
}
